// Pure rules behind the customer, site and transportation forms — no React, no store.
import { joinReferenceId } from '@/lib/utils/id';
import { SITE_FIELDS, SITE_KEYS_FIELD, siteFieldName } from '../constants/customers.constants';
import { TRANSPORT_MODES, transportFieldName } from '../constants/transportation.constants';

const isBlank = (value) => String(value ?? '').trim() === '';

export const hasRequiredFields = (values, fields) =>
  fields.every((field) => !isBlank(values?.[field]));

export const siteKeysOf = (values) => values?.[SITE_KEYS_FIELD] ?? [];

// The id one site card will save, e.g. "SI-AB12CD34"; a saved one sits whole in the number box.
export const siteReferenceId = (values, siteKey) =>
  joinReferenceId({
    idPrefix: values?.[siteFieldName(siteKey, SITE_FIELDS.idPrefix)],
    idNumber: String(values?.[siteFieldName(siteKey, SITE_FIELDS.idNumber)] ?? '').trim(),
  }).toUpperCase();

const normalizeId = (id) =>
  String(id ?? '')
    .trim()
    .toUpperCase();

/**
 * Is this card's id free — across the other cards and the customer's saved sites?
 *
 * @param existingIds  every saved site id of the customer, minus the one being edited
 */
export const isSiteIdUnique = (values, siteKey, existingIds = []) => {
  const id = siteReferenceId(values, siteKey);
  if (!normalizeId(values?.[siteFieldName(siteKey, SITE_FIELDS.idNumber)])) return true;
  const taken = new Set(existingIds.map(normalizeId));
  if (taken.has(id)) return false;
  return !siteKeysOf(values).some((key) => key !== siteKey && siteReferenceId(values, key) === id);
};

export const hasRequiredSites = (values) =>
  siteKeysOf(values).every(
    (siteKey) =>
      !isBlank(values?.[siteFieldName(siteKey, SITE_FIELDS.shippingTo)]) &&
      !isBlank(values?.[siteFieldName(siteKey, SITE_FIELDS.idNumber)]),
  );

export const hasUniqueSiteIds = (values, existingIds) =>
  siteKeysOf(values).every((siteKey) => isSiteIdUnique(values, siteKey, existingIds));

const toNumber = (value) => (isBlank(value) ? null : Number(value));

// A mode counts only when both its distance and its emission factor are real numbers.
export const modeEmissions = (values, mode) => {
  const distance = toNumber(values?.[transportFieldName(mode, 'distance')]);
  const factor = toNumber(values?.[transportFieldName(mode, 'emissionFactor')]);
  if (distance == null || factor == null || Number.isNaN(distance) || Number.isNaN(factor)) {
    return null;
  }
  return distance * factor;
};

export const hasTransportDetail = (values) =>
  TRANSPORT_MODES.some((mode) => modeEmissions(values, mode) != null);

// Sum of distance × emission factor over every complete mode; null when none is complete.
export const totalTransportEmissions = (values) => {
  const totals = TRANSPORT_MODES.map((mode) => modeEmissions(values, mode)).filter(
    (total) => total != null,
  );
  return totals.length ? totals.reduce((sum, total) => sum + total, 0) : null;
};
